import React, { useContext, useEffect, useMemo, type ReactNode } from 'react';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { OperationType } from 'relay-runtime';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { DecomposedFieldEntryPointContext } from '../../../../../../common/decomposed-field-entry-point-provider/index.tsx';

type EntryPointParams = {
	cloudId: string;
	issueKey: string;
};

type DecomposedAsyncFieldWrapperProps<EntryPointQueryType extends Record<string, OperationType>> = {
	staticView: ReactNode;
	issueKey: string;
	entryPoint: EntryPoint<EntryPointComponent<EntryPointQueryType, {}, {}, {}>, EntryPointParams>;
	fieldName: string;
};

export const DecomposedAsyncFieldWrapper = <
	EntryPointQueryType extends Record<string, OperationType>,
>({
	staticView,
	issueKey,
	entryPoint,
	fieldName,
}: DecomposedAsyncFieldWrapperProps<EntryPointQueryType>) => {
	const cloudId = useCloudId();
	const entryPointParams = useMemo(
		() => ({
			cloudId,
			issueKey,
		}),
		[cloudId, issueKey],
	);
	const runtimeProps = useMemo(() => ({}), []);

	// useEntryPoint is used here as Platform Entrypoints API does not support loading entrypoints on hover.
	// eslint-disable-next-line @atlassian/react-entrypoint/prefer-entrypoint-file-import
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		entryPoint,
		entryPointParams,
	);
	const { registerEntryPointLoad, getIsIssueKeyLoaded } = useContext(
		DecomposedFieldEntryPointContext,
	);

	// Entrypoint is not being loaded here - moving function into context
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		const deregister = registerEntryPointLoad({
			issueKey,
			fieldName,
			onEntryPointLoad: entryPointActions.load,
		});
		return deregister;
	}, [entryPointActions.load, fieldName, issueKey, registerEntryPointLoad]);

	if (!getIsIssueKeyLoaded(issueKey)) {
		return staticView;
	}

	return (
		<JiraEntryPointContainer
			id="decomposed-async-field-wrapper"
			entryPointReferenceSubject={entryPointReferenceSubject}
			runtimeProps={runtimeProps}
			fallback={staticView}
			errorFallback="flag"
		/>
	);
};
